<script lang="ts" setup>
import { ListBulletIcon, ChevronDownIcon, ChevronUpIcon } from "@heroicons/vue/24/outline";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { twMerge } from "tailwind-merge";
import { groupTypes } from "../i18n/helpers.ts";

import { SortButton, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import globals from "../globals";

const { t } = useI18n();

const props = defineProps({
  options: {
    type: Array,
    required: true,
  },
  value: {
    type: Array,
    required: false,
  },
  onSelect: {
    type: Function,
    required: true,
  },
});

const sortBy = ref("Name");
const sortDesc = ref(false);
const isCollapsed = ref(false);

function handleSortChange(column) {
  if (sortBy.value === column) {
    sortDesc.value = !sortDesc.value;
    return;
  }

  sortBy.value = column;
  sortDesc.value = false;
}

function toggleCollapse() {
  isCollapsed.value = !isCollapsed.value;
}

const sortedOptions = computed(() => {
  let sorted = [...props.options].sort(globals.smartSortFunc(globals.sortObjBy(sortBy.value)));
  return Object.freeze(sortDesc.value ? sorted.reverse() : sorted);
});
</script>

<template>
  <div v-if="sortedOptions.length === 0" class="flex flex-col space-y-2">
    <h3 class="scroll-m-20 text-2xl font-semibold tracking-tight">
      {{ t("pages.wizard.steps.users.groups.heading") }}
    </h3>

    <div class="flex flex-col items-center space-y-2 rounded-lg border bg-background p-6 shadow-sm transition-colors">
      <ListBulletIcon class="h-16 w-16" />
      <span>{{ t("pages.wizard.steps.users.groups.empty.title") }}</span>
      <router-link to="/admin/groups" class="text-sm text-muted-foreground underline">
        {{ t("pages.wizard.steps.users.groups.empty.link") }}
      </router-link>
    </div>
  </div>

  <div v-if="sortedOptions.length > 0" class="flex flex-col space-y-2">
    <button
      class="flex cursor-pointer scroll-m-20 items-center gap-2 rounded-sm text-2xl font-semibold tracking-tight transition-colors hover:text-primary"
      @click="toggleCollapse"
      @keydown.enter="toggleCollapse"
      @keydown.space.prevent="toggleCollapse"
      :aria-expanded="!isCollapsed"
      aria-controls="groups-table"
      type="button"
    >
      <component :is="isCollapsed ? ChevronDownIcon : ChevronUpIcon" class="h-6 w-6" aria-hidden="true" />
      <span>{{ t("pages.wizard.steps.users.groups.heading") }}</span>
    </button>

    <div v-show="!isCollapsed" id="groups-table" class="transition-all duration-200">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>
              <span class="sr-only">{{ t("common.select") }}</span>
            </TableHead>
            <TableHead :aria-sort="sortBy === 'Type' ? (sortDesc ? 'descending' : 'ascending') : undefined">
              <SortButton @click="handleSortChange('Type')">
                {{ t("pages.wizard.steps.users.groups.table.columns.Type") }}
              </SortButton>
            </TableHead>
            <TableHead :aria-sort="sortBy === 'Name' ? (sortDesc ? 'descending' : 'ascending') : undefined">
              <SortButton @click="handleSortChange('Name')">
                {{ t("pages.wizard.steps.users.groups.table.columns.Name") }}
              </SortButton>
            </TableHead>
            <TableHead :aria-sort="sortBy === 'Description' ? (sortDesc ? 'descending' : 'ascending') : undefined">
              <SortButton @click="handleSortChange('Description')">
                {{ t("pages.wizard.steps.users.groups.table.columns.Description") }}
              </SortButton>
            </TableHead>
            <TableHead>
              {{ t("pages.wizard.steps.users.groups.table.columns.UserCount") }}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          <TableRow
            v-for="option in sortedOptions"
            :key="option.ID"
            @click="(e) => !e.target.closest('label') && onSelect(option.ID)"
            @change="onSelect(option.ID)"
            class="cursor-pointer"
          >
            <TableCell>
              <label class="relative flex cursor-pointer items-center">
                <input
                  type="checkbox"
                  name="saml-group-select"
                  class="peer sr-only"
                  :value="option.Name"
                  :checked="value.includes(option.ID)"
                  :aria-label="`Select group '${option.Name}'`"
                />

                <span
                  aria-hidden="true"
                  :class="
                    twMerge(
                      'formkit-decorator relative block aspect-[1/1] w-[1em] select-none rounded border border-neutral-400 bg-white text-lg text-transparent shadow ring-blue-500 group-[]/multistep:shadow-none group-[]/repeater:shadow-none peer-focus-visible:ring-2 peer-focus-visible:ring-offset-1 dark:border-neutral-500 dark:bg-transparent dark:ring-offset-blue-500 dark:peer-focus-visible:ring-1 dark:peer-disabled:bg-neutral-600/50',
                      value.includes(option.ID) &&
                        'border-blue-600 bg-blue-50 text-blue-600 dark:bg-blue-900 dark:text-blue-400',
                    )
                  "
                />
              </label>
            </TableCell>
            <TableCell>
              {{ groupTypes(t)[option.Type]() }}
            </TableCell>
            <TableCell>
              {{ option.Name }}
            </TableCell>
            <TableCell>
              {{ option.Description || "-" }}
            </TableCell>
            <TableCell>
              {{ option.UserIDs ? option.UserIDs.length : 0 }}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>
  </div>
</template>
